/**
 * Customer Module API Client - Base Axios instance
 * 
 * Migrated from seo-engine-webtools-customer-module
 * All customer module service files should use this client.
 * 
 * UPDATED: Now uses core-api (8081) instead of webtools-framework (8082)
 * Customer webtools endpoints consolidated into core-api.
 */

import axios from 'axios';
import { useAuthStore } from '@/store/auth.store';
import { useSiteStore } from '@/store/site.store';
import config from '../../config';
import { applySourceSystemHeader } from '../../utils/sourceSystemHeaders';

// Uses core-api backend - customer webtools endpoints moved from webtools-framework
const CORE_API_BASE_URL = config.VITE_CORE_API_BASE_URL || config.VITE_API_BASE_URL;
const USE_MOCK_DATA = config.VITE_USE_MOCK_DATA === 'true';

export const customerApi = axios.create({
  baseURL: CORE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    sourceSystem: 'CUSTOMER_PORTAL',
  },
});

// Request interceptor to add auth + tenant headers
customerApi.interceptors.request.use(
  (config) => {
    const headers = config.headers ?? {};
    applySourceSystemHeader(headers as unknown as Record<string, unknown>);

    // Get token from auth store
    const token = useAuthStore.getState().token;
    if (token && !headers.Authorization) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Get tenant ID from auth store
    const tenantId = useAuthStore.getState().user?.tenantId;
    if (tenantId && !headers['x-tenant-id']) {
      headers['x-tenant-id'] = tenantId;
    }

    // Get site ID from site store
    const selectedSite = useSiteStore.getState().selectedSite;
    if (selectedSite?.id && !headers['x-site-id']) {
      headers['x-site-id'] = String(selectedSite.id);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
customerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Redirect to login on unauthorized
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export { USE_MOCK_DATA };
export default customerApi;
